import axios from 'axios'
import { BASE_URL } from '../constants/config'
import { getJwtToken, clearJwtTokenCache } from '../utils/jiraContext'
import { emitLicenseRequired } from '../utils/licenseManager'

/**
 * RELEASE API CLIENT
 *
 * Axios instance configured for release-related API calls.
 * Uses same JWT authentication pattern as testScenarioApi.
 */
const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 10000
})

/**
 * REQUEST INTERCEPTOR
 * Attaches JWT token to all outgoing requests.
 */
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const token = await getJwtToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    } catch (error) {
      console.error('Failed to get JWT token:', error)
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

/**
 * RESPONSE INTERCEPTOR
 * Handles global error responses, especially authentication failures.
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.response?.data?.message || error.message
    })

    // Handle license validation failures - 402 means license is invalid/expired
    if (error.response?.status === 402) {
      emitLicenseRequired(error.response?.data?.message || 'License required')
    }

    if (error.response?.status === 401) {
      console.error('Unauthorized access - clearing token cache')
      clearJwtTokenCache()
    }

    return Promise.reject(error)
  }
)

// ============================================================================
// RELEASE API FUNCTIONS
// ============================================================================

/**
 * Fetch all releases for a project (includes both active and archived)
 *
 * @param {number|string} jiraProjectId - Jira project ID
 * @returns {Promise<Array>} Array of release objects with stats
 */
export const fetchReleases = async (jiraProjectId) => {
  try {
    const response = await apiClient.get(`/releases/project/${jiraProjectId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching releases:', error)
    throw error
  }
}

/**
 * Fetch only active (non-archived) releases for a project
 *
 * @param {number|string} jiraProjectId - Jira project ID
 * @returns {Promise<Array>} Array of active release objects with stats
 */
export const fetchActiveReleases = async (jiraProjectId) => {
  try {
    const response = await apiClient.get(`/releases/project/${jiraProjectId}/active`)
    return response.data
  } catch (error) {
    console.error('Error fetching active releases:', error)
    throw error
  }
}

/**
 * Fetch only archived releases for a project
 *
 * @param {number|string} jiraProjectId - Jira project ID
 * @returns {Promise<Array>} Array of archived release objects with stats
 */
export const fetchArchivedReleases = async (jiraProjectId) => {
  try {
    const response = await apiClient.get(`/releases/project/${jiraProjectId}/archived`)
    return response.data
  } catch (error) {
    console.error('Error fetching archived releases:', error)
    throw error
  }
}

/**
 * Fetch a single release by ID
 *
 * @param {number|string} releaseId - Release ID
 * @returns {Promise<Object>} Release object with stats
 */
export const fetchReleaseById = async (releaseId) => {
  try {
    const response = await apiClient.get(`/releases/${releaseId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching release:', error)
    throw error
  }
}

/**
 * Create a new release
 *
 * @param {Object} releaseData - Release data
 * @param {string} releaseData.name - Release name
 * @param {string} releaseData.description - Release description (optional)
 * @param {string} releaseData.status - Release status (Draft, Active, Released)
 * @param {number|string} releaseData.jiraProjectId - Jira project ID
 * @returns {Promise<Object>} Created release object
 */
export const createRelease = async (releaseData) => {
  try {
    const response = await apiClient.post('/releases', releaseData)
    return response.data
  } catch (error) {
    console.error('Error creating release:', error)
    throw error
  }
}

/**
 * Update an existing release
 *
 * @param {number|string} releaseId - Release ID
 * @param {Object} updates - Fields to update { name?, description?, status? }
 * @returns {Promise<Object>} Updated release object
 */
export const updateRelease = async (releaseId, updates) => {
  try {
    const response = await apiClient.put(`/releases/${releaseId}`, updates)
    return response.data
  } catch (error) {
    console.error('Error updating release:', error)
    throw error
  }
}

/**
 * Archive a release
 *
 * @param {number|string} releaseId - Release ID
 * @returns {Promise<Object>} Updated release object
 */
export const archiveRelease = async (releaseId) => {
  try {
    const response = await apiClient.put(`/releases/${releaseId}/archive`)
    return response.data
  } catch (error) {
    console.error('Error archiving release:', error)
    throw error
  }
}

/**
 * Unarchive a release
 *
 * @param {number|string} releaseId - Release ID
 * @returns {Promise<Object>} Updated release object
 */
export const unarchiveRelease = async (releaseId) => {
  try {
    const response = await apiClient.put(`/releases/${releaseId}/unarchive`)
    return response.data
  } catch (error) {
    console.error('Error unarchiving release:', error)
    throw error
  }
}

/**
 * Delete a release
 * Note: This cascade deletes all folders and scenarios within the release
 *
 * @param {number|string} releaseId - Release ID
 * @returns {Promise<void>}
 */
export const deleteRelease = async (releaseId) => {
  try {
    const response = await apiClient.delete(`/releases/${releaseId}`)
    return response.data
  } catch (error) {
    console.error('Error deleting release:', error)
    throw error
  }
}

/**
 * Start cloning a release (async)
 * Returns a job ID to poll for completion status.
 *
 * @param {number|string} releaseId - Release ID to clone
 * @param {Object} [options] - Optional clone options
 * @param {string} [options.name] - Custom name for the cloned release
 * @param {string} [options.description] - Custom description for the cloned release
 * @returns {Promise<Object>} { jobId, status: "IN_PROGRESS" }
 */
export const cloneRelease = async (releaseId, options = null) => {
  try {
    const response = await apiClient.post(`/releases/${releaseId}/clone`, options)
    return response.data
  } catch (error) {
    console.error('Error starting clone release:', error)
    throw error
  }
}

/**
 * Poll the status of a clone release job
 *
 * @param {string} jobId - Clone job ID (UUID)
 * @returns {Promise<Object>} { jobId, status, clonedRelease?, errorMessage? }
 */
export const getCloneJobStatus = async (jobId) => {
  try {
    const response = await apiClient.get(`/releases/clone-jobs/${jobId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching clone job status:', error)
    throw error
  }
}
